/**
 * AppWalkthrough
 *
 * First-time onboarding tour for Laundry POS using driver.js.
 * Renders a step-by-step guided tour anchored to existing layout elements.
 * Fires only once per user — controlled by `hasSeenWalkthrough` flag in
 * the auth context (backed by `has_seen_walkthrough` in the DB).
 */
import { useEffect } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useAuth } from '@/context/auth-context';

const WALKTHROUGH_ENABLED = import.meta.env.VITE_ENABLE_APP_WALKTHROUGH !== 'false';

const TOUR_STEPS = [
  {
    element: '#tour-sidebar',
    popover: {
      title: '👋 Welcome to Laundry POS!',
      description:
        'This is your main navigation sidebar. Use it to switch between all sections of the app.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '#tour-nav-dashboard',
    popover: {
      title: '📊 Dashboard',
      description:
        'Your command centre. See today\'s revenue, recent orders, and key business metrics at a glance.',
      side: 'right' as const,
      align: 'center' as const,
    },
  },
  {
    element: '#tour-nav-orders',
    popover: {
      title: '🧺 Orders',
      description:
        'Create, track, and manage every laundry order from intake to delivery. Click here to get started.',
      side: 'right' as const,
      align: 'center' as const,
    },
  },
  {
    element: '#tour-nav-production',
    popover: {
      title: '🏭 Production Board',
      description:
        'The heart of your factory. Track orders through Washing, Drying, Ironing, and Packing in real-time.',
      side: 'right' as const,
      align: 'center' as const,
    },
  },
  {
    element: '#tour-nav-payments',
    popover: {
      title: '💳 Payments',
      description:
        'Manage payment history and record new transactions for pending orders.',
      side: 'right' as const,
      align: 'center' as const,
    },
  },
  {
    element: '#tour-nav-customers',
    popover: {
      title: '👥 Customers',
      description:
        'Build your customer database. Search, view order history, and manage contact information.',
      side: 'right' as const,
      align: 'center' as const,
    },
  },
  {
    element: '#tour-nav-services',
    popover: {
      title: '🏷️ Services',
      description:
        'Define your laundry services and pricing — e.g. Cuci Kering (per kg), Setrika (per pcs). Prices auto-populate in orders.',
      side: 'right' as const,
      align: 'center' as const,
    },
  },
  {
    element: '#tour-nav-reports',
    popover: {
      title: '📈 Reports',
      description:
        'View your Profit & Loss, Balance Sheet, and sales summaries. Filter by date range anytime.',
      side: 'right' as const,
      align: 'center' as const,
    },
  },
  {
    element: '#tour-nav-accounting',
    popover: {
      title: '📒 Accounting',
      description:
        'Explore journal entries and the full Chart of Accounts. Every order and payment is auto-journalised.',
      side: 'right' as const,
      align: 'center' as const,
    },
  },
  {
    element: '#tour-user-menu',
    popover: {
      title: '⚙️ Your Account',
      description:
        'Click your avatar to access Settings or to log out. You\'re all set — happy laundering! 🎉',
      side: 'bottom' as const,
      align: 'end' as const,
    },
  },
];

export function AppWalkthrough() {
  const { isAuthenticated, hasSeenWalkthrough, markWalkthroughDone } = useAuth();

  useEffect(() => {
    if (!WALKTHROUGH_ENABLED) return;

    // Only show for authenticated users who haven't seen the tour yet
    if (!isAuthenticated || hasSeenWalkthrough) return;

    let driverInstance: ReturnType<typeof driver> | null = null;

    // Small delay to let the layout fully render before attaching popovers
    const timeout = setTimeout(() => {
      driverInstance = driver({
        showProgress: true,
        showButtons: ['next', 'previous', 'close'],
        nextBtnText: 'Next →',
        prevBtnText: '← Prev',
        doneBtnText: 'Done ✓',
        progressText: 'Step {{current}} of {{total}}',
        overlayColor: '#000',
        overlayOpacity: 0.75,
        popoverClass: 'laundry-pos-tour',
        steps: TOUR_STEPS,
        onDestroyStarted: () => {
          // Fires on both "Done" click and the ✕ dismiss button
          markWalkthroughDone();
          driverInstance?.destroy();
        },
      });

      driverInstance.drive();
    }, 500);

    return () => {
      clearTimeout(timeout);
      if (driverInstance?.isActive()) {
        driverInstance.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, hasSeenWalkthrough]);

  return null;
}
